/**
 * Код ANZONIX: расчёт свеча за свечой.
 *
 * Ряды, известные на всей истории сразу («ema(закрытие, 20)»), по-прежнему
 * считаются целиком — это быстро и честно. Здесь — всё остальное: память
 * между свечами, «если … конец», циклы, свои функции, списки и рисование.
 * Код проходит свечи слева направо, как это происходило бы вживую: на
 * свече i видно только то, что было до неё и на ней, — заглянуть в
 * будущее нельзя и здесь.
 *
 * Функции рядов от величин из памяти («ema(счёт, 10)») ведут у каждого
 * места вызова свою историю: видят её так же, как увидели бы ряд целиком.
 */
import { Fail, isPure, isText } from './script-ast.js';
import { emaState, emaStep, periodIndex, POINT, rsiState, rsiStep, truth, vwapState, vwapStep, WINDOW, windowAt } from './script-math.js';
/** Сколько шагов кода допустимо за один расчёт: защита от вечного цикла. */
const MAX_OPERATIONS = 5_000_000;
/** Сколько повторов допустимо одному циклу за одну свечу. */
const MAX_LOOP = 100_000;
/** Сколько фигур каждого вида хранится: старые уходят, как в TradingView. */
export const MAX_DRAWINGS = 500;
const MAX_LIST = 10_000;
const MAX_BACK = 5_000;
export const EMPTY_DRAWINGS = { labels: [], zones: [], segments: [] };
const DRAW_COLORS = { label: '#a78bfa', zone: '#67e8f9', segment: '#94a3b8' };
const TEXT_NUMBER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const TEXT_SMALL = new Intl.NumberFormat('ru-RU', { maximumSignificantDigits: 6 });
/** Число для подписи: цены — до сотых, мелкие — с нужными знаками. */
export function textOfNumber(value, digits) {
    if (!Number.isFinite(value))
        return '—';
    const format = digits !== undefined ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: Math.max(0, Math.min(10, Math.floor(digits))) }) : Math.abs(value) >= 1 || value === 0 ? TEXT_NUMBER : TEXT_SMALL;
    return format.format(value).replace('-', '−');
}
export function runSteps(program, bars, pureEval) {
    return new Stepper(program, bars, pureEval).run();
}
class Stepper {
    program;
    bars;
    pureEval;
    n;
    i = 0;
    operations = 0;
    vectors = new Map();
    slots = new Map();
    memorySlots = [];
    ready = new Set();
    frame = new Map();
    lists = new Map();
    functions = new Map();
    sites = new Map();
    ids = new WeakMap();
    nextId = 1;
    path = '';
    returned = NaN;
    drawings = new Map();
    nextDrawing = 1;
    marks;
    alertText = '';
    /** Ячейки табло «ячейка(строка, столбец, …)»: видно последнее записанное. */
    cells = new Map();
    constructor(program, bars, pureEval) {
        this.program = program;
        this.bars = bars;
        this.pureEval = pureEval;
        this.n = bars.length;
        this.marks = { buy: new Float64Array(this.n), sell: new Float64Array(this.n), alert: new Float64Array(this.n) };
        for (const fn of program.functions)
            this.functions.set(fn.name, fn);
        for (const list of program.lists)
            this.lists.set(list, []);
        this.collectSlots(program.steps);
    }
    /** Заранее завести историю каждой величины, которую код считает по свечам. */
    collectSlots(steps) {
        for (const step of steps) {
            if ((step.kind === 'memory' || (step.kind === 'assign' && step.declare)) && !step.local && !this.slots.has(step.slot)) {
                this.slots.set(step.slot, new Float64Array(this.n).fill(NaN));
                if (step.kind === 'memory')
                    this.memorySlots.push(step.slot);
            }
            if (step.kind === 'if') {
                for (const branch of step.branches)
                    this.collectSlots(branch.body);
                if (step.otherwise)
                    this.collectSlots(step.otherwise);
            }
            if (step.kind === 'for' || step.kind === 'while')
                this.collectSlots(step.body);
        }
    }
    run() {
        const { program, n } = this;
        const plotPure = program.plots.map((plot) => isPure(plot.expr));
        const plots = program.plots.map((plot, k) => (plotPure[k] ? this.vectorOf(plot.expr) : new Float64Array(n).fill(NaN)));
        const backgroundPure = program.backgrounds.map((entry) => isPure(entry.expr));
        const backgrounds = program.backgrounds.map((entry, k) => (backgroundPure[k] ? this.vectorOf(entry.expr) : new Float64Array(n).fill(NaN)));
        const sides = [program.buy, program.sell, program.alert?.expr ?? null];
        const sidePure = sides.map((expr) => (expr ? isPure(expr) : true));
        const conditions = sides.map((expr, k) => (expr ? (sidePure[k] ? this.vectorOf(expr) : new Float64Array(n).fill(NaN)) : null));
        const table = program.table.map((row) => ({ label: row.label, text: '—', color: row.color }));
        for (let i = 0; i < n; i += 1) {
            this.i = i;
            if (i > 0)
                for (const slot of this.memorySlots)
                    this.slots.get(slot)[i] = this.slots.get(slot)[i - 1];
            this.frame = new Map();
            this.block(program.steps);
            program.plots.forEach((plot, k) => {
                if (!plotPure[k])
                    plots[k][i] = this.num(plot.expr);
            });
            program.backgrounds.forEach((entry, k) => {
                if (!backgroundPure[k])
                    backgrounds[k][i] = this.num(entry.expr);
            });
            sides.forEach((expr, k) => {
                if (expr && !sidePure[k])
                    conditions[k][i] = this.num(expr);
            });
            if (i === n - 1)
                program.table.forEach((row, k) => (table[k] = { label: row.label, text: this.text(row.expr), color: row.color }));
        }
        for (const background of backgrounds)
            for (let i = 0; i < n; i += 1)
                background[i] = truth(background[i]) ? 1 : 0;
        return {
            plots,
            backgrounds,
            buy: conditions[0] ?? null,
            sell: conditions[1] ?? null,
            alert: conditions[2] ?? null,
            marks: this.marks,
            alertText: this.alertText,
            drawings: this.collectDrawings(),
            table: [...table, ...this.cellRows()],
            slots: this.slots,
        };
    }
    vectorOf(expr) {
        const value = this.pure(expr);
        return typeof value === 'number' ? new Float64Array(this.n).fill(value) : Float64Array.from(value);
    }
    pure(expr) {
        let value = this.vectors.get(expr);
        if (value === undefined) {
            value = this.pureEval(expr);
            this.vectors.set(expr, value);
        }
        return value;
    }
    tick(at) {
        this.operations += 1;
        if (this.operations > MAX_OPERATIONS) {
            throw new Fail(at.line, at.column, 'Код считается слишком долго: больше пяти миллионов шагов. Скорее всего, цикл крутится без конца или проходит слишком много свечей на каждой свече.');
        }
    }
    /* ------------------------------------------------------------ шаги */
    block(steps) {
        for (const step of steps) {
            const flow = this.step(step);
            if (flow)
                return flow;
        }
        return null;
    }
    step(step) {
        this.tick(step);
        switch (step.kind) {
            case 'memory': {
                if (step.local)
                    throw new Fail(step.line, step.column, 'Память внутри функции не поддерживается.');
                if (!this.ready.has(step.slot)) {
                    this.slots.get(step.slot)[this.i] = this.num(step.init);
                    this.ready.add(step.slot);
                }
                return null;
            }
            case 'assign': {
                let value = this.num(step.expr);
                if (step.op !== '=') {
                    const before = this.read(step.slot, step.local);
                    value = step.op === '+' ? before + value : step.op === '-' ? before - value : step.op === '*' ? before * value : value === 0 ? NaN : before / value;
                }
                this.write(step.slot, step.local, value);
                return null;
            }
            case 'if': {
                for (const branch of step.branches) {
                    if (truth(this.num(branch.when)))
                        return this.block(branch.body);
                }
                return step.otherwise ? this.block(step.otherwise) : null;
            }
            case 'for': {
                const from = this.num(step.from);
                const to = this.num(step.to);
                if (!Number.isFinite(from) || !Number.isFinite(to))
                    return null;
                const by = step.step ? this.num(step.step) : from <= to ? 1 : -1;
                if (!Number.isFinite(by) || by === 0)
                    throw new Fail(step.line, step.column, 'Шаг цикла должен быть числом и не нулём.');
                let count = 0;
                for (let value = from; by > 0 ? value <= to : value >= to; value += by) {
                    count += 1;
                    if (count > MAX_LOOP)
                        throw new Fail(step.line, step.column, `Цикл повторился больше ${MAX_LOOP.toLocaleString('ru-RU')} раз на одной свече — проверьте «от», «до» и шаг.`);
                    this.tick(step);
                    this.frame.set(step.slot, value);
                    const flow = this.block(step.body);
                    if (flow === 'break')
                        break;
                    if (flow === 'return')
                        return flow;
                }
                return null;
            }
            case 'while': {
                let count = 0;
                while (truth(this.num(step.when))) {
                    count += 1;
                    if (count > MAX_LOOP)
                        throw new Fail(step.line, step.column, `«пока» повторилось больше ${MAX_LOOP.toLocaleString('ru-RU')} раз на одной свече: условие, видимо, никогда не становится неверным.`);
                    this.tick(step);
                    const flow = this.block(step.body);
                    if (flow === 'break')
                        break;
                    if (flow === 'return')
                        return flow;
                }
                return null;
            }
            case 'break':
                return 'break';
            case 'continue':
                return 'continue';
            case 'return':
                this.returned = step.expr ? this.num(step.expr) : NaN;
                return 'return';
            case 'do':
                this.num(step.expr);
                return null;
            case 'signal': {
                this.marks[step.side][this.i] = 1;
                if (step.side === 'alert' && step.text && !this.alertText)
                    this.alertText = this.text(step.text);
                return null;
            }
        }
    }
    read(slot, local) {
        if (local)
            return this.frame.get(slot) ?? NaN;
        return this.slots.get(slot)?.[this.i] ?? NaN;
    }
    write(slot, local, value) {
        if (local) {
            this.frame.set(slot, value);
            return;
        }
        const history = this.slots.get(slot);
        if (history)
            history[this.i] = value;
    }
    /* ------------------------------------------------------------ выражения */
    id(expr) {
        let id = this.ids.get(expr);
        if (id === undefined) {
            id = this.nextId;
            this.nextId += 1;
            this.ids.set(expr, id);
        }
        return id;
    }
    site(expr) {
        const key = `${this.path}#${this.id(expr)}`;
        let site = this.sites.get(key);
        if (!site) {
            site = { h0: [], h1: [] };
            this.sites.set(key, site);
        }
        return site;
    }
    /** Значение выражения на текущей свече. */
    num(expr) {
        if (isPure(expr)) {
            const value = this.pure(expr);
            return typeof value === 'number' ? value : value[this.i];
        }
        switch (expr.kind) {
            case 'name':
                if (expr.bind === 'local')
                    return this.frame.get(expr.slot) ?? NaN;
                return this.slots.get(expr.slot)?.[this.i] ?? NaN;
            case 'index':
                return this.back(expr.of, expr.back, expr);
            case 'at': {
                const back = Math.floor(this.num(expr.back));
                if (!Number.isFinite(back))
                    return NaN;
                if (back < 0)
                    throw new Fail(expr.line, expr.column, `В будущее смотреть нельзя: сдвиг получился ${back}. Сдвиг считается назад — 0 и больше.`);
                return this.back(expr.of, back, expr);
            }
            case 'unary': {
                const value = this.num(expr.of);
                return expr.op === '-' ? -value : truth(value) ? 0 : 1;
            }
            case 'binary':
                return this.binary(expr);
            case 'call':
                return this.call(expr);
            case 'ucall':
                return this.userCall(expr);
            case 'lcall':
                return this.listCall(expr);
            case 'draw':
                return this.draw(expr);
            case 'member':
            case 'on':
                throw new Fail(expr.line, expr.column, 'Настройки индикатора и выражение внутри «на(…)» должны быть числами или настройками, а не величинами из памяти.');
            case 'text':
            case 'color':
            case 'num':
            case 'session':
                throw new Fail(expr.line, expr.column, 'Текст и цвет бывают только подписью — в метке, табло или сообщении.');
        }
    }
    /** Значение `back` свечей назад: у имён — из их истории, у выражений — из истории места. */
    back(of, back, at) {
        if (back > MAX_BACK)
            throw new Fail(at.line, at.column, `Назад можно смотреть не дальше ${MAX_BACK} свечей.`);
        if (isPure(of)) {
            const value = this.pure(of);
            if (typeof value === 'number')
                return value;
            return this.i - back >= 0 ? value[this.i - back] : NaN;
        }
        if (of.kind === 'name') {
            if (of.bind === 'local')
                throw new Fail(at.line, at.column, `У «${of.name}» нет истории: это имя внутри функции или цикла. Историю помнят величины, заданные вне функций.`);
            const history = this.slots.get(of.slot);
            return history && this.i - back >= 0 ? history[this.i - back] : NaN;
        }
        const site = this.site(at);
        site.h0.push(this.num(of));
        const index = site.h0.length - 1 - back;
        return index >= 0 ? site.h0[index] : NaN;
    }
    binary(expr) {
        if (expr.op === 'and')
            return truth(this.num(expr.left)) && truth(this.num(expr.right)) ? 1 : 0;
        if (expr.op === 'or')
            return truth(this.num(expr.left)) || truth(this.num(expr.right)) ? 1 : 0;
        const x = this.num(expr.left);
        const y = this.num(expr.right);
        switch (expr.op) {
            case '+':
                return x + y;
            case '-':
                return x - y;
            case '*':
                return x * y;
            case '/':
                return y === 0 ? NaN : x / y;
            case '%':
                return y === 0 ? NaN : x % y;
            case '<':
                return x < y ? 1 : 0;
            case '>':
                return x > y ? 1 : 0;
            case '<=':
                return x <= y ? 1 : 0;
            case '>=':
                return x >= y ? 1 : 0;
            case '==':
                return x === y ? 1 : 0;
            case '!=':
                return Number.isFinite(x) && Number.isFinite(y) && x !== y ? 1 : 0;
            default:
                throw new Fail(expr.line, expr.column, `Неизвестное действие «${expr.op}».`);
        }
    }
    call(call) {
        const { name, args } = call;
        if (name === 'iff')
            return truth(this.num(args[0])) ? this.num(args[1]) : this.num(args[2]);
        const point = POINT[name];
        if (point)
            return point(args.map((arg) => this.num(arg)));
        const period = (index, fallback) => {
            const arg = args[index];
            const value = arg ? this.num(arg) : (fallback ?? NaN);
            return Number.isFinite(value) ? Math.max(1, Math.min(2000, Math.floor(value))) : NaN;
        };
        const site = this.site(call);
        const window = WINDOW[name];
        if (window) {
            site.h0.push(this.num(args[0]));
            const p = period(1);
            return Number.isFinite(p) ? windowAt(site.h0, site.h0.length, p, window) : NaN;
        }
        switch (name) {
            case 'ema': {
                site.ema ??= emaState();
                return emaStep(site.ema, this.num(args[0]), period(1));
            }
            case 'rsi': {
                site.rsi ??= rsiState();
                return rsiStep(site.rsi, this.num(args[0]), period(1));
            }
            case 'change':
            case 'roc':
            case 'rising':
            case 'falling': {
                const value = this.num(args[0]);
                site.h0.push(value);
                const back = name === 'change' ? period(1, 1) : period(1);
                const before = site.h0.length - 1 - back >= 0 ? site.h0[site.h0.length - 1 - back] : NaN;
                if (name === 'change')
                    return value - before;
                if (name === 'roc')
                    return before === 0 ? NaN : ((value - before) / before) * 100;
                return name === 'rising' ? (value > before ? 1 : 0) : value < before ? 1 : 0;
            }
            case 'crossup':
            case 'crossdown':
            case 'cross': {
                const a = this.num(args[0]);
                const b = this.num(args[1]);
                const pa = site.h0.length ? site.h0[site.h0.length - 1] : NaN;
                const pb = site.h1.length ? site.h1[site.h1.length - 1] : NaN;
                site.h0.push(a);
                site.h1.push(b);
                const up = a > b && pa <= pb;
                const down = a < b && pa >= pb;
                return (name === 'crossup' ? up : name === 'crossdown' ? down : up || down) ? 1 : 0;
            }
            case 'anchored_vwap':
            case 'anchored_vwap_dev': {
                site.vwap ??= vwapState();
                const price = this.num(args[0]);
                const anchor = truth(this.num(args[1]));
                return vwapStep(site.vwap, price, this.bars[this.i].volume, anchor, name === 'anchored_vwap_dev');
            }
            case 'new_period': {
                const code = this.num(args[0]);
                return this.i > 0 && periodIndex(this.bars[this.i].time, code) !== periodIndex(this.bars[this.i - 1].time, code) ? 1 : 0;
            }
            case 'barssince': {
                if (truth(this.num(args[0])))
                    site.last = this.i;
                return site.last === undefined ? NaN : this.i - site.last;
            }
            case 'valuewhen': {
                const condition = this.num(args[0]);
                const value = this.num(args[1]);
                const keep = args[2] ? Math.max(0, Math.floor(this.num(args[2]))) : 0;
                site.seen ??= [];
                if (truth(condition)) {
                    site.seen.push(value);
                    if (site.seen.length > keep + 1)
                        site.seen.shift();
                }
                return site.seen.length > keep ? site.seen[site.seen.length - 1 - keep] : NaN;
            }
            default:
                throw new Fail(call.line, call.column, `У ${name} числа в скобках должны быть постоянными — числом или настройкой, а не величиной из памяти.`);
        }
    }
    userCall(call) {
        const fn = this.functions.get(call.name);
        const values = call.args.map((arg) => this.num(arg));
        const saved = this.frame;
        const savedPath = this.path;
        this.frame = new Map();
        fn.slots.forEach((slot, k) => this.frame.set(slot, values[k]));
        this.path = `${savedPath}>${this.id(call)}`;
        this.returned = NaN;
        this.tick(call);
        const flow = this.block(fn.body);
        const result = flow === 'return' ? this.returned : NaN;
        this.frame = saved;
        this.path = savedPath;
        return result;
    }
    listCall(call) {
        const list = this.lists.get(call.list);
        const arg = (k) => this.num(call.args[k]);
        const at = (k, forWrite) => {
            const raw = Math.floor(arg(k));
            const index = raw < 0 ? list.length + raw : raw;
            if (forWrite && (!Number.isFinite(index) || index < 0 || index >= list.length)) {
                throw new Fail(call.line, call.column, `В списке «${call.list}» ${list.length} ${list.length === 1 ? 'элемент' : 'элементов'}, а номер ${raw}. Номера идут с 0; −1 — последний.`);
            }
            return index;
        };
        switch (call.name) {
            case 'push': {
                if (list.length >= MAX_LIST)
                    throw new Fail(call.line, call.column, `В списке «${call.list}» уже ${MAX_LIST.toLocaleString('ru-RU')} элементов — уберите старые («убрать_первый»), прежде чем добавлять.`);
                list.push(arg(0));
                return list.length;
            }
            case 'size':
                return list.length;
            case 'get': {
                const index = at(0, false);
                return index >= 0 && index < list.length ? list[index] : NaN;
            }
            case 'set': {
                const index = at(0, true);
                list[index] = arg(1);
                return list[index];
            }
            case 'remove': {
                const index = at(0, true);
                return list.splice(index, 1)[0];
            }
            case 'shift':
                return list.length ? list.shift() : NaN;
            case 'pop':
                return list.length ? list.pop() : NaN;
            case 'clear':
                list.length = 0;
                return 0;
            case 'first':
                return list.length ? list[0] : NaN;
            case 'last':
                return list.length ? list[list.length - 1] : NaN;
            case 'indexof':
                return list.indexOf(arg(0));
            case 'includes':
                return list.includes(arg(0)) ? 1 : 0;
            case 'list_max':
                return list.length ? Math.max(...list) : NaN;
            case 'list_min':
                return list.length ? Math.min(...list) : NaN;
            case 'list_sum':
                return list.reduce((sum, x) => sum + x, 0);
            case 'list_avg':
                return list.length ? list.reduce((sum, x) => sum + x, 0) / list.length : NaN;
            default:
                throw new Fail(call.line, call.column, `Не знаю действия «${call.name}» со списком.`);
        }
    }
    /* ------------------------------------------------------------ рисование */
    draw(call) {
        const nums = call.args.map((arg) => this.num(arg));
        const text = call.text ? this.text(call.text) : '';
        const bar = (back) => Math.max(0, this.i - Math.max(0, Math.floor(Number.isFinite(back) ? back : 0)));
        switch (call.name) {
            case 'label': {
                const [price, back = 0] = nums;
                if (!Number.isFinite(price))
                    return NaN;
                return this.add({ kind: 'label', data: { index: bar(back), price: price, text, color: call.color ?? DRAW_COLORS.label } });
            }
            case 'box': {
                const [back, top, bottom, right] = nums;
                if (!Number.isFinite(top) || !Number.isFinite(bottom))
                    return NaN;
                // Правый край задан — зона кончается на нём; нет — живёт, пока её не закроют.
                const to = right === undefined || !Number.isFinite(right) ? null : bar(right);
                return this.add({ kind: 'zone', data: { from: bar(back), to, top: Math.max(top, bottom), bottom: Math.min(top, bottom), color: call.color ?? DRAW_COLORS.zone, text } });
            }
            case 'segment': {
                const [back1, price1, back2, price2] = nums;
                if (!Number.isFinite(price1) || !Number.isFinite(price2))
                    return NaN;
                return this.add({ kind: 'segment', data: { from: bar(back1), fromPrice: price1, to: bar(back2), toPrice: price2, color: call.color ?? DRAW_COLORS.segment, text } });
            }
            case 'close': {
                const drawing = this.drawings.get(Math.floor(nums[0]));
                if (drawing?.kind === 'zone' && drawing.data.to === null)
                    drawing.data.to = this.i;
                return 0;
            }
            case 'delete':
                this.drawings.delete(Math.floor(nums[0]));
                return 0;
            case 'cell': {
                const row = Math.floor(nums[0]);
                const col = Math.floor(nums[1]);
                if (!(row >= 0 && row < 200 && col >= 0 && col < 8))
                    return NaN;
                this.cells.set(`${row}:${col}`, { row, col, text, color: call.color });
                return 0;
            }
            case 'extend': {
                const drawing = this.drawings.get(Math.floor(nums[0]));
                const to = bar(nums[1] ?? 0);
                if (drawing?.kind === 'zone')
                    drawing.data.to = Math.max(drawing.data.from, to);
                if (drawing?.kind === 'segment')
                    drawing.data.to = Math.max(drawing.data.from, to);
                return 0;
            }
            case 'top':
            case 'bottom': {
                const drawing = this.drawings.get(Math.floor(nums[0]));
                if (!drawing)
                    return NaN;
                if (drawing.kind === 'zone')
                    return call.name === 'top' ? drawing.data.top : drawing.data.bottom;
                if (drawing.kind === 'label')
                    return drawing.data.price;
                return call.name === 'top' ? drawing.data.fromPrice : drawing.data.toPrice;
            }
            default:
                throw new Fail(call.line, call.column, `Не знаю, как нарисовать «${call.name}».`);
        }
    }
    add(drawing) {
        const id = this.nextDrawing;
        this.nextDrawing += 1;
        this.drawings.set(id, drawing);
        let count = 0;
        for (const entry of this.drawings.values())
            if (entry.kind === drawing.kind)
                count += 1;
        if (count > MAX_DRAWINGS) {
            for (const [key, entry] of this.drawings) {
                if (entry.kind === drawing.kind) {
                    this.drawings.delete(key);
                    break;
                }
            }
        }
        return id;
    }
    /** Ячейки — строками табло: столбец 0 — подпись, остальные — значение через «·». Пустые строки не показываются. */
    cellRows() {
        const rows = new Map();
        for (const cell of this.cells.values())
            rows.set(cell.row, [...(rows.get(cell.row) ?? []), cell]);
        return [...rows]
            .sort((a, b) => a[0] - b[0])
            .map(([, cells]) => {
            const sorted = cells.sort((a, b) => a.col - b.col);
            const first = sorted[0];
            const rest = first.col === 0 ? sorted.slice(1) : sorted;
            const label = first.col === 0 ? first.text : '';
            return { label, text: rest.map((cell) => cell.text).filter(Boolean).join(' · '), color: rest.find((cell) => cell.color)?.color ?? null };
        })
            .filter((row) => row.label !== '' || row.text !== '');
    }
    collectDrawings() {
        const labels = [];
        const zones = [];
        const segments = [];
        for (const drawing of this.drawings.values()) {
            if (drawing.kind === 'label')
                labels.push({ ...drawing.data });
            else if (drawing.kind === 'zone')
                zones.push({ ...drawing.data });
            else
                segments.push({ ...drawing.data });
        }
        return { labels, zones, segments };
    }
    /* ------------------------------------------------------------ текст */
    text(expr) {
        switch (expr.kind) {
            case 'text':
                return expr.value;
            case 'call':
                if (expr.name === 'tostr')
                    return textOfNumber(this.num(expr.args[0]), expr.args[1] ? this.num(expr.args[1]) : undefined);
                if (expr.name === 'iff' && isText(expr))
                    return truth(this.num(expr.args[0])) ? this.text(expr.args[1]) : this.text(expr.args[2]);
                break;
            case 'binary':
                if (expr.op === '+' && isText(expr))
                    return this.text(expr.left) + this.text(expr.right);
                break;
            default:
                break;
        }
        return textOfNumber(this.num(expr));
    }
}
//# sourceMappingURL=script-steps.js.map